import React from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import styled from 'styled-components'

import useIsServerSideContext from '../../../hooks/useIsServerSideContext'

import TrackVisibility from '../../../components/utils/TrackVisibility'
import WithSelectEscape from '../../../components/SelectEscape/WithSelectEscape'

import SuggestedAdventureCard, { LoadingCard } from './SuggestedAdventureCard'

const Wrapper = styled.div`
  margin: 0 -14px;
`

const Slide = styled.div`
  padding: 0 14px;
  max-width: 324px;
  box-sizing: border-box;
`

const defaultParams = {
  slidesPerView: 2,
  slidesPerGroup: 2,
  shouldSwiperUpdate: true,
  speed: 600,
  breakpoints: {
    768: {
      slidesPerView: 3,
      slidesPerGroup: 3,
    },
    992: {
      slidesPerView: 4,
      slidesPerGroup: 4,
    },
  },
}

interface AdventureCardSliderProps {
  getSwiper: React.ComponentProps<typeof Swiper>['onSwiper']
  loading?: boolean
  canFetchMore?: boolean
  onFetchMore?: (...args: any) => any
  bookingAdventuresIndex: GQL_BookingAdventuresIndex
  bucketListItemsIndex: GQL_BucketListItemsIndex
  adventures: GQL_AdventureCardFragmentFragment[]
  inItineraryContext?: boolean
  maxSlides?: number
  showReviewScore?: boolean
}

const AdventureCardSlider = ({
  getSwiper,
  loading,
  canFetchMore,
  onFetchMore,
  bookingAdventuresIndex,
  bucketListItemsIndex,
  adventures,
  inItineraryContext,
  showReviewScore,
  maxSlides,
}: AdventureCardSliderProps) => {
  const isServer = useIsServerSideContext()
  const swiperParams = {
    ...defaultParams,
  }
  if (maxSlides) {
    swiperParams.breakpoints[992] = {
      slidesPerView: maxSlides,
      slidesPerGroup: maxSlides,
    }
  }

  const adventureSlides =
    adventures?.map((adventure) => (
      <Slide key={adventure.id}>
        <WithSelectEscape
          itemId={adventure.id}
          itemType="Adventure"
          bookingAdventuresIndex={bookingAdventuresIndex}
          bucketListItemsIndex={bucketListItemsIndex}
        >
          <SuggestedAdventureCard
            {...adventure}
            inItineraryContext={inItineraryContext}
            showReviewScore={showReviewScore}
          />
        </WithSelectEscape>
      </Slide>
    )) || []

  const loadingSlides = loading
    ? [...Array(4)].map((_, i) => (
        // eslint-disable-next-line react/jsx-indent,react/no-array-index-key
        <Slide className="swiper-slide" key={i}>
          <LoadingCard />
        </Slide>
      ))
    : []

  const fetchMoreTriggerSlides =
    !loading && canFetchMore
      ? [
          <TrackVisibility
            onChange={(isVisible) => {
              if (isVisible) {
                onFetchMore()
              }
            }}
          >
            <LoadingCard />
          </TrackVisibility>,
        ]
      : []

  const ssrSlide = (
    <Slide className="swiper-slide">
      <LoadingCard />
    </Slide>
  )

  const slides = isServer
    ? [ssrSlide]
    : [...adventureSlides, ...fetchMoreTriggerSlides, ...loadingSlides]

  return (
    <Wrapper>
      <Swiper onSwiper={getSwiper} {...swiperParams}>
        {slides.map((slide) => (
          <SwiperSlide key={`slide-${slide?.key}`}>{slide}</SwiperSlide>
        ))}
      </Swiper>
    </Wrapper>
  )
}

export default AdventureCardSlider
